import { digitAtPower, powersForValue } from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import {
    buildBooleanPlaceholderWheel,
    buildNaturalPlaceholderWheel,
    buildOperandWheel,
    buildResultWheel,
    buildWholeFlightWheel,
    dimStableWheelRoles,
    toWheelLayout,
    toWheelPosition,
    type PrimitiveWheelLayout,
} from '@/adapters/primitive/_pure/derivation/wheels';
import { momentKey, stepMoment } from '@/adapters/primitive/_pure/derivation/primitiveMoment';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

function countedThroughDigitByPower(value: number): Partial<Record<number, number>> {
    return Object.fromEntries(
        powersForValue(value).map((power) => [power, digitAtPower({ value, power })]),
    );
}

function stableWheels(args: {
    config: Config;
    layout: PrimitiveWheelLayout;
    input: number;
    settled: boolean;
    completed?: boolean;
}): PrimitiveWheelState[] {
    const wheels: PrimitiveWheelState[] = [
        buildOperandWheel({
            layout: args.layout,
            role: 'identity-input',
            value: args.input,
        }),
        args.settled
            ? buildResultWheel({
                layout: args.layout,
                role: 'identity-output',
                powers: powersForValue(args.input),
                countedThroughDigitByPower: countedThroughDigitByPower(args.input),
            })
            : buildNaturalPlaceholderWheel({ layout: args.layout, role: 'identity-output' }),
    ];
    return args.completed
        ? dimStableWheelRoles({
            config: args.config,
            wheels,
            roles: ['identity-input'],
        })
        : wheels;
}

function flightWheel(args: {
    config: Config;
    input: number;
    position: [number, number, number];
    opacity?: number;
}): PrimitiveWheelState {
    return buildWholeFlightWheel({
        role: 'identity-input',
        value: args.input,
        bands: powersForValue(args.input).map((power) => ({
            power,
            countedSlots: Array.from(
                { length: digitAtPower({ value: args.input, power }) },
                (_, index) => index + 1,
            ),
            overhangSlots: [],
            marker: null,
        })),
        position: args.position,
        opacity: args.opacity,
    });
}

export function deriveNaturalIdentityStepStates(args: {
    config: Config;
    input: number;
}): PrimitiveStepState[] {
    const layout = toWheelLayout({
        config: args.config,
        operandSchedulingMode: args.config.natural.operandSchedulingMode,
    });
    const sourcePosition = toWheelPosition({ layout, role: 'identity-input' });
    const targetPosition = toWheelPosition({ layout, role: 'identity-output' });
    const before = stableWheels({ config: args.config, layout, input: args.input, settled: false });
    const completed = stableWheels({
        config: args.config,
        layout,
        input: args.input,
        settled: true,
        completed: true,
    });

    return [
        {
            ...momentKey(stepMoment('initial')),
            label: 'Start',
            description: 'Input and empty Out are ready.',
            keyframes: [{ wheels: before, transition: 'instant', events: [] }],
        },
        {
            ...momentKey(stepMoment('transfer-identity-wheel')),
            label: 'Transfer Identity',
            description: 'Copy the complete Input wheel directly onto Out.',
            keyframes: [
                {
                    wheels: [
                        ...dimStableWheelRoles({
                            config: args.config,
                            wheels: before,
                            roles: ['identity-input'],
                        }),
                        flightWheel({ ...args, position: sourcePosition }),
                    ],
                    transition: 'instant',
                    events: [],
                },
                {
                    wheels: [
                        ...dimStableWheelRoles({
                            config: args.config,
                            wheels: before,
                            roles: ['identity-input'],
                        }),
                        flightWheel({ ...args, position: targetPosition }),
                    ],
                    transition: { durationMs: args.config.base.animation?.flightBaseDurationMs ?? 0 },
                    events: [],
                },
                { wheels: completed, transition: 'instant', events: [] },
            ],
        },
    ];
}

function booleanWheel(args: {
    layout: PrimitiveWheelLayout;
    role: 'identity-input' | 'identity-output';
    value: boolean;
    variant?: 'flight';
    position?: [number, number, number];
    opacity?: number;
}): PrimitiveWheelState {
    return {
        valueKind: 'boolean',
        role: args.role,
        value: args.value,
        powers: [0],
        position: args.position ?? toWheelPosition({ layout: args.layout, role: args.role }),
        ...(args.variant === undefined ? {} : { variant: args.variant }),
        ...(args.opacity === undefined ? {} : { bandOpacityByPower: { 0: args.opacity } }),
    };
}

export function deriveBooleanIdentityStepStates(args: {
    config: Config;
    input: boolean;
}): PrimitiveStepState[] {
    const layout = toWheelLayout({
        config: args.config,
        operandSchedulingMode: args.config.natural.operandSchedulingMode,
    });
    const sourcePosition = toWheelPosition({ layout, role: 'identity-input' });
    const targetPosition = toWheelPosition({ layout, role: 'identity-output' });
    const initial = [
        booleanWheel({ ...args, layout, role: 'identity-input', value: args.input }),
        buildBooleanPlaceholderWheel({ layout, role: 'identity-output' }),
    ];
    const completed = dimStableWheelRoles({
        config: args.config,
        wheels: [
            booleanWheel({ ...args, layout, role: 'identity-input', value: args.input }),
            booleanWheel({ ...args, layout, role: 'identity-output', value: args.input }),
        ],
        roles: ['identity-input'],
    });

    return [
        {
            ...momentKey(stepMoment('initial')),
            label: 'Start',
            description: 'Boolean In and empty Out are ready.',
            keyframes: [{ wheels: initial, transition: 'instant', events: [] }],
        },
        {
            ...momentKey(stepMoment('transfer-identity-wheel')),
            label: 'Transfer Identity',
            description: 'Copy the complete Boolean In wheel directly onto Out.',
            keyframes: [
                {
                    wheels: [
                        ...dimStableWheelRoles({
                            config: args.config,
                            wheels: initial,
                            roles: ['identity-input'],
                        }),
                        booleanWheel({
                            ...args,
                            layout,
                            role: 'identity-input',
                            value: args.input,
                            variant: 'flight',
                            position: sourcePosition,
                        }),
                    ],
                    transition: 'instant',
                    events: [],
                },
                {
                    wheels: [
                        ...dimStableWheelRoles({
                            config: args.config,
                            wheels: initial,
                            roles: ['identity-input'],
                        }),
                        booleanWheel({
                            ...args,
                            layout,
                            role: 'identity-input',
                            value: args.input,
                            variant: 'flight',
                            position: targetPosition,
                        }),
                    ],
                    transition: { durationMs: args.config.base.animation?.flightBaseDurationMs ?? 0 },
                    events: [],
                },
                { wheels: completed, transition: 'instant', events: [] },
            ],
        },
    ];
}
